import type { ColumnDef } from '@tanstack/react-table';

import IconButton from 'components/partials/button/icon-button';

import Icons from 'assets/icons';

import type { AdmitAdjustmentRow } from '../../hooks/use-admit-adjustments';

import { formatRate } from '../../helpers/results-format';

import EditablePercentCell from './editable-percent-cell';

const commonHeaderStyles =
  'border-r-2 border-white font-semibold uppercase cursor-default text-xs py-2.5 px-2.5';

interface AdmitAdjustmentsTableParams {
  onAdmitChange: (row: AdmitAdjustmentRow, value: number) => void;
  onYieldChange: (row: AdmitAdjustmentRow, value: number) => void;
  onUndo: (row: AdmitAdjustmentRow) => void;
}

export const getAdmitAdjustmentsTableSettings = ({
  onAdmitChange,
  onYieldChange,
  onUndo,
}: AdmitAdjustmentsTableParams): ColumnDef<AdmitAdjustmentRow>[] => [
  {
    id: 'academicRank',
    accessorKey: 'academicRank',
    header: () => <span>Academic rank</span>,
    cell: ({ row }) => (
      <span className="text-sm font-semibold text-gray-700">
        {row.original.academicRank}
      </span>
    ),
    headerClassName: `${commonHeaderStyles} rounded-tl-md rounded-bl-md`,
    className: 'w-[19%] px-2.5 py-1.5',
    enableSorting: false,
  },
  {
    id: 'pyAdmits',
    accessorKey: 'pyAdmits',
    header: () => <span>PY admits</span>,
    cell: ({ row }) => (
      <span className="text-sm text-gray-700">
        {row.original.pyAdmits.toLocaleString()}
      </span>
    ),
    headerClassName: commonHeaderStyles,
    className: 'w-[12%] px-2.5 py-1.5',
    enableSorting: false,
  },
  {
    id: 'admitMultiplier',
    accessorKey: 'admitMultiplier',
    header: () => <span>% Increase</span>,
    cell: ({ row }) => (
      <EditablePercentCell
        value={row.original.admitMultiplier}
        onChange={(val) => onAdmitChange(row.original, val)}
        isDirty={
          row.original.admitMultiplier !== row.original.baselineAdmitMultiplier
        }
      />
    ),
    headerClassName: commonHeaderStyles,
    className: 'w-[14%] px-2.5 py-1.5',
    enableSorting: false,
  },
  {
    id: 'admitTarget',
    accessorKey: 'admitTarget',
    header: () => <span>Admit target</span>,
    cell: ({ row }) => (
      <span className="text-sm text-gray-700">
        {row.original.admitTarget.toLocaleString()}
      </span>
    ),
    headerClassName: commonHeaderStyles,
    className: 'w-[14%] px-2.5 py-1.5',
    enableSorting: false,
  },
  {
    id: 'yieldMultiplier',
    accessorKey: 'yieldMultiplier',
    header: () => <span>% Yield</span>,
    cell: ({ row }) => (
      <EditablePercentCell
        value={row.original.yieldMultiplier}
        onChange={(val) => onYieldChange(row.original, val)}
        isDirty={
          row.original.yieldMultiplier !== row.original.baselineYieldMultiplier
        }
      />
    ),
    headerClassName: commonHeaderStyles,
    className: 'w-[14%] px-2.5 py-1.5',
    enableSorting: false,
  },
  {
    id: 'pyYield',
    accessorKey: 'pyYield',
    header: () => <span>PY yield</span>,
    cell: ({ row }) => (
      <span className="text-sm text-gray-700">
        {formatRate(row.original.pyYield)}
      </span>
    ),
    headerClassName: commonHeaderStyles,
    className: 'w-[12%] px-2.5 py-1.5',
    enableSorting: false,
  },
  {
    id: 'projYield',
    accessorKey: 'projYield',
    header: () => <span>Proj yield</span>,
    cell: ({ row }) => (
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-700">
          {formatRate(row.original.projYield)}
        </span>
        {row.original.isDirty && (
          <IconButton
            variant="reserve-text"
            size="xs"
            icon={<Icons.Undo />}
            aria-label="Undo row changes"
            onClick={() => onUndo(row.original)}
          />
        )}
      </div>
    ),
    headerClassName: `${commonHeaderStyles} rounded-tr-md rounded-br-md`,
    className: 'w-[15%] px-2.5 py-1.5',
    enableSorting: false,
  },
];
